import Link from "next/link"

import { Chip } from "@/components/ui/chip"
import { cn } from "@/lib/utils"

import type { SidebarRowDef } from "./items"
import { texts } from "./localization/texts"

type SidebarRowProps = {
  row: SidebarRowDef
  isCollapsed?: boolean
  isActive?: boolean
}

export const sidebarItemClassName =
  "group flex min-h-11 w-full items-center gap-2.5 rounded-lg px-1 text-sm font-normal text-white/50"

export const sidebarItemCollapsedClassName = "justify-center gap-0 px-0"

export const sidebarIconClassName =
  "size-6 max-h-6 max-w-6 shrink-0 overflow-visible text-white opacity-50 [&_path]:fill-current [&_path]:[fill-opacity:1] group-hover:opacity-80"

export function SidebarRow({
  row,
  isCollapsed = false,
  isActive = false
}: SidebarRowProps) {
  const Icon = row.icon
  const label = texts.rows[row.id]
  const content = (
    <>
      <span className="flex size-6 shrink-0 items-center justify-center overflow-visible">
        <Icon
          className={cn(sidebarIconClassName, isActive && "opacity-100")}
          width={24}
          height={24}
          aria-hidden
        />
      </span>
      {isCollapsed ? null : (
        <>
          <span className="min-w-0 flex-1 truncate text-left font-medium text-[12px]">
            {label}
          </span>
          {row.badge ? (
            <Chip variant={row.badge.variant}>
              {row.badge.variant === "count"
                ? row.badge.value
                : texts.badges[row.badge.textKey]}
            </Chip>
          ) : null}
        </>
      )}
    </>
  )

  const className = cn(
    sidebarItemClassName,
    isCollapsed && sidebarItemCollapsedClassName,
    row.href !== null && "hover:text-white/80",
    isActive && "bg-black-600 text-white hover:text-white"
  )

  if (row.href === null) {
    return (
      <div className={className} title={label}>
        {content}
      </div>
    )
  }

  return (
    <Link
      href={row.href}
      aria-label={label}
      aria-current={isActive ? "page" : undefined}
      title={label}
      className={className}
    >
      {content}
    </Link>
  )
}
